import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import type { GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { hasPermission } from '@relif/zeller-roles';
import { AddIcon } from 'assets/icons';
import CustomDataGrid from 'components/common/CustomDataGrid';
import MasterFilter, { useMasterFilter } from 'components/common/MasterFilter';
import type { FilterFieldConfig } from 'components/common/MasterFilter/types';
import { useSelectedBusinessUnit } from 'contexts/SidebarContext';
import useCurrentUser from 'hooks/useCurrentUser';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetUserBusinessUnitsQuery } from 'services/api/businessUnits.api';
import { useDeleteUserMutation, useGetUsersQuery } from 'services/api/user.api';
import type { User, WorkingHours } from 'services/types/user';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  MOBILE_PAGE_PT,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  SHADOW,
  SURFACE,
  YELLOW,
} from 'styles/tokens';
import removeCSVInjection from 'utils/removeCSVInjection';

import UserFormModal from './UserFormModal';

const BOOLEAN_OPTIONS = [
  { value: 'true', label: 'Sí' },
  { value: 'false', label: 'No' },
];

const ROLE_OPTIONS = [
  { value: 'seller', label: 'Vendedor' },
  { value: 'sellerWithAssignment', label: 'Vendedor con Asignación' },
  { value: 'branchManager', label: 'Jefe de Sucursal' },
  { value: 'businessUnitAdmin', label: 'Administrador de Negocio' },
];

const SELLER_MANAGEMENT_ATTRIBUTES: GridColDef<User>[] = [
  {
    field: 'brandsToSell',
    headerName: 'Marcas que vende',
    width: 200,
    valueGetter: (brandsToSell?: string[] | null) => {
      return brandsToSell?.join(', ');
    },
  },
  {
    field: 'branchOffice',
    headerName: 'Sucursal',
    width: 200,
  },
  {
    field: 'topicsOfInterest',
    headerName: 'Tags que atiende',
    width: 200,
    valueGetter: (topicsOfInterest?: string[] | null) => {
      return topicsOfInterest?.join(', ');
    },
  },
  {
    field: 'sources',
    headerName: 'Orígenes',
    width: 200,
    valueGetter: (sources?: string[] | null) => {
      return sources?.join(', ');
    },
  },
];

const DAY_LABELS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

const formatHourLabel = (h: number): string => {
  const hh = Math.floor(h) % 24;
  const mm = h - Math.floor(h) === 0.5 ? '30' : '00';
  return `${hh.toString().padStart(2, '0')}:${mm}`;
};

const formatWorkingHours = (wh?: WorkingHours | null): string => {
  if (!wh || wh.length === 0) return '24/7';
  // Order: Mon..Sat, then Sun
  const order = [1, 2, 3, 4, 5, 6, 0];
  const dayMap: Record<number, { start: number; end: number }> = {};
  wh.forEach((range) => {
    range.days.forEach((d) => {
      if (dayMap[d] === undefined) dayMap[d] = { start: range.start, end: range.end };
    });
  });
  const parts = order
    .filter((d) => dayMap[d])
    .map((d) => {
      const { start, end } = dayMap[d];
      return `${DAY_LABELS[d]} ${formatHourLabel(start)}-${formatHourLabel(end)}`;
    });
  return parts.length ? parts.join(', ') : 'Sin horario';
};

const USER_ATTRIBUTES: GridColDef<User>[] = [
  {
    field: 'name',
    headerName: 'Nombre',
    width: 150,
    valueGetter: (name?: string | null, row?: User) => {
      return `${row?.isAdmin ? '[ADMIN] ' : ''}${name || '-'}`;
    },
  },
  {
    field: 'email',
    headerName: 'Email',
    width: 300,
  },
  {
    field: 'phone',
    headerName: 'Teléfono',
    width: 150,
  },
  {
    field: 'businessUnitNames',
    headerName: 'Unidades de negocio',
    width: 250,
    valueGetter: (businessUnitNames?: string[] | null) => {
      return businessUnitNames?.join(', ');
    },
  },
  {
    field: 'role',
    headerName: 'Rol',
    width: 200,
    valueGetter: (role?: string | null) => {
      if (role === 'seller') return 'Vendedor';
      if (role === 'sellerWithAssignment') return 'Vendedor con Asignación';
      if (role === 'branchManager') return 'Jefe de Sucursal';
      if (role === 'businessUnitAdmin') return 'Administrador de Negocio';
      return role?.toUpperCase();
    },
  },
  {
    field: 'isAssignable',
    headerName: 'Asignable',
    width: 120,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => {
      const row = params.row as User;
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            width: '100%',
            height: '100%',
          }}
        >
          {params.value ? (
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 22 }} />
          ) : (
            <CancelIcon sx={{ color: 'error.main', fontSize: 22 }} />
          )}
          {row.isAway && (
            <Tooltip title="Fuera de oficina">
              <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main', flexShrink: 0 }} />
            </Tooltip>
          )}
        </Box>
      );
    },
  },
  {
    field: 'workingHours',
    headerName: 'Horario',
    width: 260,
    valueGetter: (workingHours?: WorkingHours | null) => formatWorkingHours(workingHours),
  },
  {
    field: 'allowedTemplateNames',
    headerName: 'Plantillas Permitidas',
    width: 250,
    valueGetter: (_: unknown, row?: User) => {
      if (row?.allowAllTemplates) {
        return 'Todos';
      }
      if (row?.allowedTemplateNames && row.allowedTemplateNames?.length > 0) {
        return row.allowedTemplateNames?.join(', ');
      }
      return 'Ninguno';
    },
  },
  {
    field: 'allowTemplateCreation',
    headerName: 'Puede crear plantillas',
    width: 160,
    valueFormatter: (value) => (value ? 'Si' : 'No'),
  },
  {
    field: 'externalId',
    headerName: 'ID sistema externo',
    width: 160,
  },
];

const USER_ATTRIBUTES_ADMIN: GridColDef<User>[] = [
  {
    field: 'id',
    headerName: 'ID',
    width: 60,
  },
  {
    field: 'company',
    headerName: 'Empresa',
    width: 200,
    valueGetter: (company?: { id: number; name: string } | null) => {
      return company?.name;
    },
  },
  {
    field: 'companyId',
    headerName: 'Empresa',
    width: 100,
  },
  {
    field: 'filterConversationsByDefault',
    headerName: 'Filtrar conversaciones activado?',
    width: 300,
    valueFormatter: (value) => (value ? 'Si' : 'No'),
  },
  {
    field: 'description',
    headerName: 'Descripción',
    width: 200,
  },
];

const DEFAULT_PAGE_SIZE = 50;

const parsePaginationParam = (raw: string | null): GridPaginationModel => {
  if (!raw) return { page: 0, pageSize: DEFAULT_PAGE_SIZE };
  try {
    const parsed = JSON.parse(raw) as GridPaginationModel;
    if (typeof parsed?.page === 'number' && typeof parsed?.pageSize === 'number' && parsed.pageSize <= 100) {
      return parsed;
    }
  } catch {
    /* fall through */
  }
  return { page: 0, pageSize: DEFAULT_PAGE_SIZE };
};

const parseSortParam = (raw: string | null): GridSortModel => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as GridSortModel;
    if (Array.isArray(parsed)) return parsed;
  } catch {
    /* fall through */
  }
  return [];
};

const getSortValue = (row: User, column: GridColDef<User> | undefined): unknown => {
  if (!column) return undefined;
  const raw = row[column.field as keyof User];
  if (column.valueGetter) {
    return column.valueGetter(raw as never, row, column, {} as never);
  }
  return raw;
};

const compareValues = (a: unknown, b: unknown): number => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return a === b ? 0 : a ? 1 : -1;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

const UsersTable: FC = () => {
  const { selectedBusinessUnitId } = useSelectedBusinessUnit();
  const businessUnitId =
    selectedBusinessUnitId != null && selectedBusinessUnitId !== -1 ? selectedBusinessUnitId : undefined;

  const { data: users, refetch } = useGetUsersQuery({ businessUnitId });
  const { data: businessUnits } = useGetUserBusinessUnitsQuery(null);
  const [deleteUser] = useDeleteUserMutation();
  const currentUser = useCurrentUser();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(() =>
    parsePaginationParam(searchParams.get('paginationModel'))
  );
  const [sortModel, setSortModel] = useState<GridSortModel>(() => parseSortParam(searchParams.get('sortModel')));

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const isDefault = paginationModel.page === 0 && paginationModel.pageSize === DEFAULT_PAGE_SIZE;
        if (isDefault) {
          prev.delete('paginationModel');
        } else {
          prev.set('paginationModel', JSON.stringify(paginationModel));
        }
        return prev;
      },
      { replace: true }
    );
  }, [paginationModel, setSearchParams]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        if (sortModel.length === 0) {
          prev.delete('sortModel');
        } else {
          prev.set('sortModel', JSON.stringify(sortModel));
        }
        return prev;
      },
      { replace: true }
    );
  }, [sortModel, setSearchParams]);

  const hasSellerManagement = businessUnits?.some((businessUnit) => businessUnit.hasSellerManagement) ?? false;
  const isAdmin = currentUser?.isAdmin ?? false;

  const usersFilterConfig = useMemo<FilterFieldConfig[]>(() => {
    const config: FilterFieldConfig[] = [
      {
        key: 'searchTerm',
        kind: 'text',
        label: 'Buscar',
        placement: 'bar',
        placeholder: 'Buscar nombre, email...',
      },
      { key: 'role', kind: 'singleSelect', label: 'Rol', placement: 'bar', options: ROLE_OPTIONS },
      {
        key: 'isAssignable',
        kind: 'singleSelect',
        label: 'Asignable',
        placement: 'modal',
        options: BOOLEAN_OPTIONS,
      },
      {
        key: 'allowAllTemplates',
        kind: 'singleSelect',
        label: 'Acceso a todas las plantillas',
        placement: 'modal',
        options: BOOLEAN_OPTIONS,
      },
      {
        key: 'allowTemplateCreation',
        kind: 'singleSelect',
        label: 'Puede crear plantillas',
        placement: 'modal',
        options: BOOLEAN_OPTIONS,
      },
    ];
    if (hasSellerManagement) {
      config.push(
        { key: 'brandsToSell', kind: 'multiSelect', label: 'Marcas que vende', placement: 'modal' },
        { key: 'branchOffice', kind: 'multiSelect', label: 'Sucursal', placement: 'modal' },
        { key: 'topicsOfInterest', kind: 'multiSelect', label: 'Temas de interés', placement: 'modal' },
        { key: 'sources', kind: 'multiSelect', label: 'Orígenes', placement: 'modal' }
      );
    }
    if (isAdmin) {
      config.push(
        { key: 'company', kind: 'singleSelect', label: 'Empresa', placement: 'modal' },
        {
          key: 'filterConversationsByDefault',
          kind: 'singleSelect',
          label: 'Filtra conversaciones por defecto',
          placement: 'modal',
          options: BOOLEAN_OPTIONS,
        }
      );
    }
    return config;
  }, [hasSellerManagement, isAdmin]);

  const filter = useMasterFilter(usersFilterConfig, selectedBusinessUnitId);
  const searchText = (filter.values.searchTerm as string) ?? '';
  const roleFilter = (filter.values.role as string | null) ?? null;
  const isAssignableFilter = (filter.values.isAssignable as string | null) ?? null;
  const allowAllTemplatesFilter = (filter.values.allowAllTemplates as string | null) ?? null;
  const allowTemplateCreationFilter = (filter.values.allowTemplateCreation as string | null) ?? null;
  const brandsFilter = (filter.values.brandsToSell as string[] | undefined) ?? [];
  const branchOfficeFilter = (filter.values.branchOffice as string[] | undefined) ?? [];
  const topicsFilter = (filter.values.topicsOfInterest as string[] | undefined) ?? [];
  const sourcesFilter = (filter.values.sources as string[] | undefined) ?? [];
  const companyFilter = (filter.values.company as string | null) ?? null;
  const filterConversationsFilter = (filter.values.filterConversationsByDefault as string | null) ?? null;

  const dynamicOptions = useMemo(() => {
    const brands = new Set<string>();
    const branches = new Set<string>();
    const topics = new Set<string>();
    const srcs = new Set<string>();
    const companies = new Map<string, string>();
    (users ?? []).forEach((u) => {
      u.brandsToSell?.forEach((b) => b && brands.add(b));
      if (u.branchOffice) branches.add(u.branchOffice);
      u.topicsOfInterest?.forEach((t) => t && topics.add(t));
      u.sources?.forEach((s) => s && srcs.add(s));
      if (u.company?.id != null && u.company?.name) companies.set(String(u.company.id), u.company.name);
    });
    return {
      brandsToSell: [...brands].sort().map((b) => ({ value: b, label: b })),
      branchOffice: [...branches].sort().map((b) => ({ value: b, label: b })),
      topicsOfInterest: [...topics].sort().map((t) => ({ value: t, label: t })),
      sources: [...srcs].sort().map((s) => ({ value: s, label: s })),
      company: [...companies.entries()]
        .sort((a, b) => a[1].localeCompare(b[1]))
        .map(([id, name]) => ({ value: id, label: name })),
    };
  }, [users]);

  const canCreate = !!currentUser && hasPermission(currentUser, 'users', 'create', null);

  const filteredUsers = useMemo(() => {
    const usersRows = (users || []).map((user) => ({
      ...user,
      email: removeCSVInjection(user.email || ''),
    }));

    const searchLower = searchText.toLowerCase();
    const matchBool = (flag: string | null, value: boolean | null | undefined) =>
      flag === null || (flag === 'true' ? !!value : !value);

    return usersRows.filter((user) => {
      if (roleFilter && user.role !== roleFilter) return false;
      if (!matchBool(isAssignableFilter, user.isAssignable)) return false;
      if (!matchBool(allowAllTemplatesFilter, user.allowAllTemplates)) return false;
      if (!matchBool(allowTemplateCreationFilter, user.allowTemplateCreation)) return false;
      if (!matchBool(filterConversationsFilter, user.filterConversationsByDefault)) return false;
      if (brandsFilter.length && !brandsFilter.some((b) => user.brandsToSell?.includes(b))) return false;
      if (branchOfficeFilter.length && (!user.branchOffice || !branchOfficeFilter.includes(user.branchOffice)))
        return false;
      if (topicsFilter.length && !topicsFilter.some((t) => user.topicsOfInterest?.includes(t))) return false;
      if (sourcesFilter.length && !sourcesFilter.some((s) => user.sources?.includes(s))) return false;
      if (companyFilter && String(user.company?.id ?? '') !== companyFilter) return false;
      if (searchText) {
        const userString = Object.values(user).join(' ');
        if (!userString.toLowerCase().includes(searchLower)) return false;
      }
      return true;
    });
  }, [
    users,
    searchText,
    roleFilter,
    isAssignableFilter,
    allowAllTemplatesFilter,
    allowTemplateCreationFilter,
    filterConversationsFilter,
    brandsFilter,
    branchOfficeFilter,
    topicsFilter,
    sourcesFilter,
    companyFilter,
  ]);

  const sortableColumnsByField = useMemo(() => {
    const cols: GridColDef<User>[] = [
      ...USER_ATTRIBUTES,
      ...(isAdmin ? USER_ATTRIBUTES_ADMIN : []),
      ...(isAdmin || hasSellerManagement ? SELLER_MANAGEMENT_ATTRIBUTES : []),
    ];
    return new Map(cols.map((c) => [c.field, c]));
  }, [isAdmin, hasSellerManagement]);

  const sortedUsers = useMemo(() => {
    if (sortModel.length === 0) return filteredUsers;
    const rows = [...filteredUsers];
    rows.sort((a, b) => {
      for (const { field, sort } of sortModel) {
        if (!sort) continue;
        const col = sortableColumnsByField.get(field);
        const cmp = compareValues(getSortValue(a, col), getSortValue(b, col));
        if (cmp !== 0) return sort === 'asc' ? cmp : -cmp;
      }
      return 0;
    });
    return rows;
  }, [filteredUsers, sortModel, sortableColumnsByField]);

  const totalPages = Math.max(1, Math.ceil(sortedUsers.length / paginationModel.pageSize));
  const safePage = Math.min(paginationModel.page, totalPages - 1);

  useEffect(() => {
    if (paginationModel.page !== safePage) {
      setPaginationModel((prev) => ({ ...prev, page: safePage }));
    }
  }, [paginationModel.page, safePage]);

  const paginatedUsers = useMemo(() => {
    const start = safePage * paginationModel.pageSize;
    return sortedUsers.slice(start, start + paginationModel.pageSize);
  }, [sortedUsers, safePage, paginationModel.pageSize]);

  const handleCreateUser = () => {
    setSelectedUser(null);
    setModalOpen(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setModalOpen(true);
  };

  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await deleteUser(userToDelete.id).unwrap();
      await refetch();
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      console.error('[UsersTable] Error deleting user:', error);
    }
  };

  const actionsColumn: GridColDef<User> = {
    field: 'actions',
    headerName: 'Acciones',
    width: 100,
    sortable: false,
    filterable: false,
    renderCell: (params) => {
      const user = params.row as User;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {currentUser && hasPermission(currentUser, 'users', 'update', user) && (
            <Tooltip title="Editar usuario">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditUser(user);
                }}
                sx={{ color: alpha(NAVY, 0.55), '&:hover': { color: NAVY, bgcolor: alpha(NAVY, 0.06) } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {currentUser && hasPermission(currentUser, 'users', 'delete', user) && (
            <Tooltip title="Eliminar usuario">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteUser(user);
                }}
                sx={{ color: alpha(NAVY, 0.55), '&:hover': { color: 'error.main', bgcolor: alpha(NAVY, 0.06) } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      );
    },
  };

  const userAttributes: GridColDef[] = currentUser?.isAdmin
    ? [...USER_ATTRIBUTES, ...USER_ATTRIBUTES_ADMIN, ...SELLER_MANAGEMENT_ATTRIBUTES, actionsColumn]
    : [...USER_ATTRIBUTES, ...(hasSellerManagement ? SELLER_MANAGEMENT_ATTRIBUTES : []), actionsColumn];

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: SURFACE,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: { xs: 2, lg: 3 }, pt: { xs: `${MOBILE_PAGE_PT}px`, md: 2, lg: 3 }, flexShrink: 0 }}>
        <MasterFilter {...filter} dynamicOptions={dynamicOptions} />
      </Box>

      {/* Data Grid */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 2, lg: 3 },
          pt: 1.5,
          pb: { xs: 2, lg: 3 },
        }}
      >
        {canCreate && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
            <Button
              startIcon={<AddIcon />}
              onClick={handleCreateUser}
              sx={{
                height: 36,
                borderRadius: RADIUS_COIN,
                backgroundColor: YELLOW,
                color: NAVY,
                textTransform: 'none',
                fontFamily: 'Jost, sans-serif',
                fontWeight: 700,
                fontSize: '0.8rem',
                px: 2,
                whiteSpace: 'nowrap',
                '&:hover': { backgroundColor: `${YELLOW}cc` },
              }}
            >
              Crear Usuario
            </Button>
          </Box>
        )}
        {!users && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
            <Skeleton variant="rectangular" height={40} />
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={48} />
            ))}
          </Box>
        )}
        {filteredUsers && users && (
          <Fade in timeout={300}>
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <CustomDataGrid<User>
                rows={paginatedUsers}
                columns={userAttributes}
                filterModel={{ items: [] }}
                sortModel={sortModel}
                onFilterModelChange={() => {}}
                onSortModelChange={setSortModel}
                paginationModel={{ page: safePage, pageSize: paginationModel.pageSize }}
                onPaginationModelChange={setPaginationModel}
                rowCount={sortedUsers.length}
                getRowClassName={() => ''}
                onRowClick={(user) => handleEditUser(user)}
                pageId="users"
                disableFilters
                disableColumnOrder
              />
            </Box>
          </Fade>
        )}
      </Box>

      {/* Create/Edit Modal */}
      <UserFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        user={selectedUser}
        onSuccess={() => {
          refetch().catch((error) => console.error('[UsersTable] Error refetching users:', error));
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        slotProps={{
          paper: {
            sx: {
              backdropFilter: GLASS_BLUR,
              backgroundColor: GLASS_BACKGROUND,
              boxShadow: SHADOW,
              borderRadius: RADIUS_CARD,
              border: `1px solid ${alpha(NAVY, 0.06)}`,
            },
          },
        }}
      >
        <DialogTitle sx={{ color: NAVY, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon sx={{ color: 'error.main' }} />
          Confirmar Eliminación
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: alpha(NAVY, 0.75) }}>
            ¿Estás seguro de que deseas eliminar al usuario &quot;{userToDelete?.name || userToDelete?.email}&quot;?
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ borderRadius: RADIUS_COIN, color: NAVY, textTransform: 'none', px: 2.5 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              confirmDelete().catch(console.error);
            }}
            color="error"
            variant="contained"
            sx={{
              borderRadius: RADIUS_COIN,
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: 'none',
              px: 2.5,
              '&:hover': { boxShadow: 'none' },
            }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersTable;
